/**
=========================================================
* Trust Cargo - Containers
=========================================================

* One tab per container type - "Mix goods", "Light goods", whatever the
* office adds next - because a type is a row in container_types, not a value
* baked into this file. New tabs are created from the toolbar.
*
* Types are a schema change (see database/container_types.sql). Until it has
* been applied the type endpoints answer with an empty list, and this page
* renders exactly as it did before: one flat list, no tabs, nothing broken.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { Table, TableCell, TableHead, TableBody, TableRow } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import {
  FilterSelect,
  FilterDates,
  optionsFromRows,
  matches,
  inDateRange,
} from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";
import { boardAction } from "utils/board";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api";
const V2 = `${API}/v2`;

// the tab for containers that have not been given a type
const UNTYPED = "none";

function Containers() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  // the new/rename type dialog: {id, name} or null
  const [typeForm, setTypeForm] = useState(null);
  const [typeError, setTypeError] = useState("");
  const [status, setStatus] = useState("");
  const [telex, setTelex] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const navigate = useNavigate();

  // types only exist once the schema change has been applied
  const typed = types.length > 0;

  const fetchData = async () => {
    try {
      /**
       * Prefer the endpoint that carries container_type_id. Falling back to
       * the legacy list keeps the page working on a server that has not been
       * updated - it simply has no types to group by.
       */
      const [t, c] = await Promise.all([
        fetch(`${V2}/container-types`, { cache: "no-store" })
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => []),
        fetch(`${V2}/typed-containers`, { cache: "no-store" }).catch(() => null),
      ]);

      setTypes(Array.isArray(t) ? t : []);

      if (c && c.ok) {
        setRows(await c.json());
      } else {
        const legacy = await fetch(`${API}/containers`).then((r) => r.json());
        setRows(legacy.containers || []);
      }
    } catch (e) {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const byTab = (() => {
    if (tab === "all") return rows;
    if (tab === UNTYPED) return rows.filter((r) => !r.container_type_id);
    return rows.filter((r) => String(r.container_type_id) === String(tab));
  })();

  const narrowed = byTab.filter(
    (c) =>
      matches(c.status, status) &&
      matches(c.telex_status, telex) &&
      inDateRange(c.loading_date, from, to)
  );

  const filteredRows = filterRows(narrowed, query);
  const pager = usePagination(filteredRows);

  const untypedCount = rows.filter((r) => !r.container_type_id).length;
  const typeName = (id) => types.find((t) => String(t.id) === String(id))?.name;

  const removeOne = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete container ${row.name}?`)) return;

    try {
      await axios.post(
        `${API}/remove_container`,
        { id: row.id },
        { headers: { Accept: "application/json" } }
      );
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
  };

  /** Create or rename a type, i.e. add or rename a tab. */
  const saveType = async () => {
    const name = String(typeForm?.name || "").trim();
    if (!name) return;

    setBusy(true);
    setTypeError("");

    try {
      if (typeForm.id) {
        await axios.put(`${V2}/container-types/${typeForm.id}`, { name });
      } else {
        await axios.post(`${V2}/container-types`, { name });
      }
      setTypeForm(null);
      await fetchData();
    } catch (e) {
      setTypeError(e?.response?.data?.message || "Could not save the type");
    }
    setBusy(false);
  };

  const deleteType = async (t) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the "${t.name}" tab? Its containers move to Not set.`)) return;

    setBusy(true);
    try {
      await axios.delete(`${V2}/container-types/${t.id}`);
      if (String(tab) === String(t.id)) setTab("all");
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete the type");
    }
    setBusy(false);
  };

  const cell = { fontSize: 14 };

  const tabSx = (active) => ({
    fontWeight: active ? 700 : 500,
    cursor: "pointer",
    backgroundColor: active ? "rgba(201,162,39,0.18)" : "transparent",
    border: "1px solid rgba(11,27,58,0.15)",
  });

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            <FilterBar
              title="Containers"
              value={query}
              onChange={setQuery}
              count={filteredRows.length}
              total={byTab.length}
              placeholder="Search by name, number or status..."
              actions={
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setTypeError("");
                      setTypeForm({ name: "" });
                    }}
                  >
                    New Type
                  </Button>
                  <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                    Add Container
                  </Button>
                </>
              }
            >
              <FilterSelect
                label="Status"
                icon="flag"
                value={status}
                onChange={setStatus}
                options={optionsFromRows(rows, "status")}
              />
              <FilterSelect
                label="Telex"
                icon="description"
                value={telex}
                onChange={setTelex}
                options={optionsFromRows(rows, "telex_status")}
              />
              <FilterDates label="Loading" from={from} to={to} onFrom={setFrom} onTo={setTo} />

              {/* one tab per type; each carries its own count */}
              <Chip
                size="small"
                label={`All (${rows.length})`}
                onClick={() => setTab("all")}
                sx={tabSx(tab === "all")}
              />

              {types.map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  label={`${t.name} (${t.containers})`}
                  onClick={() => setTab(t.id)}
                  onDelete={() => deleteType(t)}
                  deleteIcon={<Icon sx={{ fontSize: "16px !important" }}>close</Icon>}
                  sx={tabSx(String(tab) === String(t.id))}
                />
              ))}

              {typed && untypedCount > 0 && (
                <Chip
                  size="small"
                  label={`Not set (${untypedCount})`}
                  onClick={() => setTab(UNTYPED)}
                  sx={tabSx(tab === UNTYPED)}
                />
              )}
            </FilterBar>

            {!typed && (
              <SuiBox px={3} pb={2}>
                <Alert severity="info">
                  Container types are not set up on the server yet, so there are no tabs. Run
                  database/container_types.sql once and upload the backend files.
                </Alert>
              </SuiBox>
            )}

            {loading ? (
              <CargoLoader label="Loading containers" />
            ) : (
              <>
                <SuiBox sx={{ overflowX: "auto" }}>
                  <Table sx={{ minWidth: 1050 }} aria-label="containers table">
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Name</TableCell>
                        <TableCell align="center">Number</TableCell>
                        {typed && <TableCell align="center">Type</TableCell>}
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Loading</TableCell>
                        <TableCell align="center">Est. arrival</TableCell>
                        <TableCell align="center">Operations</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pager.pageRows.map((row) => (
                        <TableRow key={row.id}>
                          <IdCell value={row.id} />
                          <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                            {row.name ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.number && row.number !== "0" ? row.number : "-"}
                          </TableCell>

                          {typed && (
                            <TableCell align="center" sx={cell}>
                              {typeName(row.container_type_id) ? (
                                <Chip
                                  label={typeName(row.container_type_id)}
                                  size="small"
                                  sx={({ palette, functions: { rgba } }) => ({
                                    fontWeight: 700,
                                    backgroundColor: rgba(palette.info.main, 0.14),
                                    color: palette.info.main,
                                  })}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          )}

                          <TableCell align="center" sx={cell}>
                            {row.status ? (
                              <Chip
                                label={row.status}
                                size="small"
                                sx={({ palette, functions: { rgba } }) => ({
                                  fontWeight: 700,
                                  textTransform: "capitalize",
                                  backgroundColor: rgba(palette.primary.main, 0.16),
                                  color: palette.dark.main,
                                })}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            {row.loading_date ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.estimated_arrival_date ?? "-"}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <RowActions
                              actions={[
                                boardAction("container", row, fetchData),
                                {
                                  icon: "visibility",
                                  label: "View container",
                                  tone: "info",
                                  onClick: () => navigate(`/view_container/${row.id}`),
                                },
                                {
                                  icon: "list_alt",
                                  label: "Orders in this container",
                                  tone: "primary",
                                  onClick: () => navigate(`/container_orders/${row.id}`),
                                },
                                {
                                  icon: "edit",
                                  label: "Edit container",
                                  onClick: () => setEditingId(row.id),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete",
                                  tone: "danger",
                                  onClick: () => removeOne(row),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={typed ? 8 : 7} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No containers
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </SuiBox>
                <Pagination {...pager} />
              </>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      {/* the container form goes through the typed endpoints when they exist,
          so the type is saved with the rest in one request */}
      <AddDialog
        open={adding}
        entity={typed ? "container_typed" : "container"}
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      <AddDialog
        open={Boolean(editingId)}
        entity={typed ? "container_typed" : "container"}
        recordId={editingId}
        onClose={() => setEditingId(null)}
        onCreated={fetchData}
      />

      {/* add or rename a tab */}
      <Dialog open={Boolean(typeForm)} onClose={() => setTypeForm(null)} fullWidth maxWidth="xs">
        <DialogTitle>{typeForm?.id ? "Rename type" : "New container type"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {typeError && (
            <SuiBox mb={2}>
              <Alert severity="error">{typeError}</Alert>
            </SuiBox>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Type name"
            placeholder="Mix goods"
            value={typeForm?.name || ""}
            onChange={(e) => setTypeForm((f) => ({ ...f, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && saveType()}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-root": { minHeight: 52 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTypeForm(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={saveType} disabled={busy}>
            {busy ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Containers;
